// API layer. Acquires an Entra access token for the backend API scope (when
// configured) and attaches it as a bearer token. When Entra isn't configured
// (local dev against an AUTH_DISABLED backend), calls go out without a token.

import { ID_SCOPES } from "./authConfig";

const API_BASE = process.env.REACT_APP_API_BASE || "";

// Acquire a fresh Microsoft sign-in (ID) token and send it as the bearer. The
// backend validates it (audience = this SPA app, email domain = nursepartners.net).
async function getToken(msalInstance, account) {
  if (!account) return null;
  try {
    const res = await msalInstance.acquireTokenSilent({ scopes: ID_SCOPES, account });
    return res.idToken;
  } catch {
    const res = await msalInstance.acquireTokenPopup({ scopes: ID_SCOPES, account });
    return res.idToken;
  }
}

async function request(path, { msalInstance, account, method = "GET", body, extraHeaders } = {}) {
  const headers = { "Content-Type": "application/json", ...(extraHeaders || {}) };
  const token = msalInstance ? await getToken(msalInstance, account) : null;
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, {
    method, headers, body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    const err = new Error(b.error || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export function fetchDashboard(ctx, month) {
  const q = month ? `?month=${encodeURIComponent(month)}` : "";
  return request(`/api/dashboard${q}`, ctx);
}

export function refreshDashboard(ctx) {
  return request(`/api/dashboard/refresh`, { ...ctx, method: "POST" });
}

export function fetchFinancials(ctx) {
  return request(`/api/financials`, ctx);
}

// Upload a single-month QuickBooks P&L export (.xlsx, base64) to finalize that
// month's actuals. The backend parses it, stores an override, and marks the
// month Actual across the P&L and Executive tabs.
export function uploadPnl(ctx, base64, filename) {
  return request(`/api/pnl/upload`, { ...ctx, method: "POST", body: { file: base64, filename } });
}

// Upload a Teambridge CSV export (sent as JSON text) to refresh the dashboard.
export function uploadCsv(ctx, csvText, filename) {
  return request(`/api/upload`, { ...ctx, method: "POST", body: { csv: csvText, filename } });
}

export function fetchInsights(ctx) {
  return request(`/api/insights`, ctx);
}

// Overtime & supervisory shift tracking (live Teambridge API). May return
// { warming: true } on the first load after a cold start — retry shortly.
export function fetchOvertime(ctx, month) {
  const q = month ? `?month=${encodeURIComponent(month)}` : "";
  return request(`/api/overtime${q}`, ctx);
}

// ── Monthly final-report archive ─────────────────────────────────────────────
export function captureReport(ctx, month) {
  return request(`/api/reports/capture`, { ...ctx, method: "POST", body: { month } });
}
export function fetchReports(ctx) {
  return request(`/api/reports`, ctx);
}
// Fetch the archived PDF as a blob (with auth), for viewing/downloading.
export async function fetchReportBlob({ msalInstance, account }, month) {
  const token = msalInstance ? await getToken(msalInstance, account) : null;
  const res = await fetch(`${API_BASE}/api/reports/${month}/pdf`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error("Failed to load the report PDF.");
  return res.blob();
}

// Executive data is only returned by the backend when the passcode is correct.
export function fetchExecutive(ctx, passcode) {
  return request(`/api/financials/executive`, { ...ctx, method: "POST", body: { passcode } });
}

// Save the owner's editable "additional details" note on the Executive analysis.
// The backend enforces that only the owner's account may write it.
export function saveExecNote(ctx, passcode, note) {
  return request(`/api/financials/executive/note`, { ...ctx, method: "POST", body: { passcode, note } });
}

// Recruitment board reports (Executive tab). Passcode-gated; may return
// { warming: true } on a cold start — retry shortly.
export function fetchRecruitment(ctx, passcode, month) {
  return request(`/api/recruitment`, { ...ctx, method: "POST", body: { passcode, month } });
}

// Census / client-retention board reports (Executive tab). Passcode-gated.
export function fetchCensus(ctx, passcode, month) {
  return request(`/api/census`, { ...ctx, method: "POST", body: { passcode, month } });
}

// Board report PDF (Financials + Recruitment + Census) as a blob, for ownership
// to download and send to board members, who have no NursePartners account.
export async function fetchBoardReportBlob({ msalInstance, account }, passcode, month) {
  const token = msalInstance ? await getToken(msalInstance, account) : null;
  const res = await fetch(`${API_BASE}/api/executive/board-report.pdf`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ passcode, month }),
  });
  if (!res.ok) {
    let msg = "Could not build the board report.";
    try { msg = (await res.json()).error || msg; } catch { /* non-JSON error body */ }
    throw new Error(msg);
  }
  const disp = res.headers.get("Content-Disposition") || "";
  const match = disp.match(/filename="([^"]+)"/);
  return { blob: await res.blob(), filename: match ? match[1] : "NursePartners Board Report.pdf" };
}
